import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Company } from '@tacliatech/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionService } from '../permissions';
import { toolsStructure } from './company-module.const';

interface ToolApiResponse {
  id: string;
  name: string;
  icon: string;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyToolsService {
  private toolsStorage = new BehaviorSubject<Company.Tool[]>(null);

  tools$ = this.toolsStorage.asObservable();

  activeTools$ = this.tools$.pipe(
    map((res) => (res === null ? null : res.filter((t) => t.active)))
  );

  idActiveTools$ = this.activeTools$.pipe(
    map((res) => res?.map((t) => t.id) || [])
  );

  constructor(
    private http: HttpClient,
    private permissionService: PermissionService
  ) {}

  private fetchTools(): Promise<ToolApiResponse[]> {
    return this.http
      .get<ToolApiResponse[]>(':API_BASE_URL/company/tools')
      .toPromise()
      .catch(() => []);
  }

  private calculateCanAccess(tool: Company.Tool): boolean {
    const permissions = Company.ToolRoutesPermissionsMap[tool.id];
    if (!permissions || permissions.length === 0) {
      return true;
    }
    return permissions.some((p) =>
      this.permissionService.permissions.includes(p.feature)
    );
  }

  async init(): Promise<void> {
    const apiTools = await this.fetchTools();

    const tools = toolsStructure.map((tool) => {
      const isInApi = apiTools.some((t) => t.name === tool.id);
      const canAccess = isInApi && this.calculateCanAccess(tool);
      return {
        ...tool,
        active: isInApi && canAccess,
        canAccess,
      };
    });

    this.toolsStorage.next(tools);
  }

  clearToolsStorage(): void {
    this.toolsStorage.next(null);
  }

  public hasToolAccess$(
    toolIds: Company.IdTool[],
    allRequired = false
  ): Observable<boolean> {
    return this.idActiveTools$.pipe(
      map((ids) =>
        allRequired
          ? toolIds.every((id) => ids.includes(id))
          : toolIds.some((id) => ids.includes(id))
      )
    );
  }
}
